/*
 * 犬｜食材の内訳（スープごはんの3食・6食・12食）
 *
 * inu-meal-choices スニペットが出す <inu-meal-choices> の中身を受け持つ。
 *
 * 1. 合計が食数と一致するまでカートに入れさせない。
 *    同じ食材の上限（食数の半分）も見る。
 *    1種類限定（設定の single）では、1種類を選ぶまでカートに入れさせない。
 * 2. 選んだ内訳を1行にまとめ、注文メモ（properties）へ入れる。
 * 3. バリアントが変わったら選択欄を描き直す。
 *    Horizon はバリアントを切り替えてもバリアントピッカー自身しか差し替えないため、
 *    これが無いと「3食のときの入力欄」が6食に残る。
 *    内訳が要らないバリアント（1食）へ移ったら欄ごと消し、注文メモも送らない。
 */

import { StandardEvents } from '@shopify/events';

class InuMealChoices extends HTMLElement {
  /** @type {Element | null} */
  #scope = null;

  connectedCallback() {
    // 商品ページはセクション、モーダルは dialog が変更イベントの通り道になる
    this.#scope = this.closest('.shopify-section, dialog');
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener('input', this.#handleInput);
    this.addEventListener('click', this.#handleStep);
    this.#sync();
  }

  disconnectedCallback() {
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.removeEventListener('input', this.#handleInput);
    this.removeEventListener('click', this.#handleStep);
  }

  /**
   * スニペットが埋め込んだ設定と、バリアントごとの食数
   * @returns {{ label: string, note: string, unit: string, single: boolean, choices: string[], messages: Record<string, string>, variants: Record<string, { total: number, maxEach: number, available: boolean }> } | null}
   */
  get #data() {
    const script = this.querySelector('script[type="application/json"][data-inu-meal-data]');
    if (!script?.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[inu-meal-choices] 設定を読めませんでした', error);
      return null;
    }
  }

  /** 今のバリアントの決まりごと */
  get #rule() {
    const data = this.#data;
    const key = this.dataset.variant ?? '';
    return data?.variants?.[key] ?? null;
  }

  #handleInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.hasAttribute('data-inu-meal-input') && !target.hasAttribute('data-inu-meal-pick')) return;
    this.#sync();
  };

  /** ＋ / − ボタン */
  #handleStep = (event) => {
    const button = event.target.closest('[data-inu-meal-step]');
    if (!button) return;

    const input = button.parentElement?.querySelector('[data-inu-meal-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const step = Number(button.getAttribute('data-inu-meal-step')) || 0;
    const max = Number(input.max) || 0;
    const next = Math.min(Math.max((Number(input.value) || 0) + step, 0), max);
    input.value = String(next);
    this.#sync();
  };

  #handleProductSelect = (event) => {
    // detail の入れ子は @shopify/events の版によって変わるので、両方の形を見る
    const variantId = event.detail?.variantId ?? event.detail?.detail?.variantId;

    if (variantId) {
      this.setVariant(variantId);
      return;
    }

    // Horizon はセクションを取り直したあとにカートボタンを差し替える。
    // 先にこちらが disabled にしても戻されるので、取り直しの完了後にもう一度そろえる
    event.promise
      ?.then(({ detail }) => {
        const resolvedId = detail?.resource?.id;
        if (resolvedId) this.setVariant(resolvedId);
        this.#sync();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[inu-meal-choices] バリアントを特定できませんでした', error);
      });
  };

  /**
   * 指定したバリアントの選択欄に描き直す。モーダルの select からも呼ぶ
   * @param {string | number} variantId
   */
  setVariant(variantId) {
    const key = String(variantId);
    if (this.dataset.variant === key) return;

    const data = this.#data;
    if (!data?.variants?.[key]) return;

    this.dataset.variant = key;

    const current = this.querySelector('[data-inu-meals-field], [data-inu-meals-fixed]');
    const rule = data.variants[key];

    // 内訳が要らないバリアントでは欄ごと出さない
    if (rule.total === 0 && !rule.fixed) {
      current?.remove();
      this.#sync();
      return;
    }

    // 1種類限定は食数が変わっても選択肢は同じなので、選んだものを引き継ぐ
    if (data.single && rule.total > 0) {
      const picked = this.#pickedChoice();
      const next = this.#buildSingleField(data, picked);
      if (current) {
        current.replaceWith(next);
      } else {
        this.insertBefore(next, this.querySelector('[data-inu-meal-property]'));
      }
      this.#sync();
      return;
    }

    // 中身が決まっている食数は、選ばせずに内訳だけ伝える
    const next = rule.total === 0 ? this.#buildFixed(data, rule) : this.#buildField(data, rule);
    if (current) {
      current.replaceWith(next);
    } else {
      // hidden の前に置く。欄は必ず注文メモより上に出す
      this.insertBefore(next, this.querySelector('[data-inu-meal-property]'));
    }
    this.#sync();
  }

  /**
   * 中身が決まっている食数の案内。選べると思って探されないように出す
   * @param {{ choices: string[], unit: string, messages: Record<string, string> }} data
   * @param {{ fixed: number }} rule
   */
  #buildFixed(data, rule) {
    const note = document.createElement('p');
    note.className = 'inu-meals__fixed';
    note.setAttribute('data-inu-meals-fixed', '');
    note.textContent = (data.messages.fixed || '{choices}を各{n}{unit}お届けします')
      .replace('{choices}', data.choices.join('・'))
      .replace('{n}', String(rule.fixed))
      .replace('{unit}', data.unit);
    return note;
  }

  /** 1種類限定でいま選ばれている食材。未選択なら空文字 */
  #pickedChoice() {
    const picked = this.querySelector('[data-inu-meal-pick]:checked');
    return picked instanceof HTMLInputElement ? picked.value : '';
  }

  /**
   * 1種類だけ選ばせる欄。マークアップは inu-meal-choices スニペットと同じ形にそろえる
   * @param {{ label: string, note: string, choices: string[], messages: Record<string, string> }} data
   * @param {string} picked - 引き継ぐ選択。空文字なら未選択のまま出す
   */
  #buildSingleField(data, picked) {
    const uid = this.dataset.uid ?? '';

    const field = document.createElement('fieldset');
    field.className = 'inu-meals inu-meals--single';
    field.setAttribute('data-inu-meals-field', '');

    const legend = document.createElement('legend');
    legend.className = 'inu-meals__legend';
    legend.textContent = data.label;
    field.appendChild(legend);

    const note = document.createElement('p');
    note.className = 'inu-meals__note';
    note.textContent = data.note;
    field.appendChild(note);

    const list = document.createElement('ul');
    list.className = 'inu-meals__list';
    list.setAttribute('role', 'list');

    for (const choice of data.choices) {
      const item = document.createElement('li');
      item.className = 'inu-meals__item inu-meals__item--pick';

      const label = document.createElement('label');
      label.className = 'inu-meals__pick';

      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.className = 'inu-meals__radio';
      radio.name = `${uid}-meal-pick`;
      radio.value = choice;
      radio.setAttribute('data-inu-meal-pick', '');
      radio.checked = choice === picked;
      label.appendChild(radio);

      const name = document.createElement('span');
      name.className = 'inu-meals__pick-name';
      name.textContent = choice;
      label.appendChild(name);

      item.appendChild(label);
      list.appendChild(item);
    }
    field.appendChild(list);

    const alert = document.createElement('p');
    alert.className = 'inu-meals__alert';
    alert.setAttribute('data-inu-meal-alert', '');
    alert.setAttribute('role', 'status');
    alert.setAttribute('aria-live', 'polite');
    field.appendChild(alert);

    return field;
  }

  /**
   * 選択欄を組み立てる。マークアップは inu-meal-choices スニペットと同じ形にそろえる
   * @param {{ label: string, note: string, unit: string, choices: string[] }} data
   * @param {{ total: number, maxEach: number }} rule
   */
  #buildField(data, rule) {
    const uid = this.dataset.uid ?? '';
    const inputMax = rule.maxEach > 0 ? rule.maxEach : rule.total;

    const field = document.createElement('fieldset');
    field.className = 'inu-meals';
    field.setAttribute('data-inu-meals-field', '');

    const legend = document.createElement('legend');
    legend.className = 'inu-meals__legend';
    legend.textContent = data.label;
    field.appendChild(legend);

    const note = document.createElement('p');
    note.className = 'inu-meals__note';
    note.textContent = data.note;
    field.appendChild(note);

    const list = document.createElement('ul');
    list.className = 'inu-meals__list';
    list.setAttribute('role', 'list');

    data.choices.forEach((choice, index) => {
      list.appendChild(this.#buildItem(uid, choice, index + 1, inputMax, data.unit));
    });
    field.appendChild(list);

    const total = document.createElement('p');
    total.className = 'inu-meals__total';

    const totalLabel = document.createElement('span');
    totalLabel.className = 'inu-meals__total-label';
    totalLabel.textContent = '合計';

    const totalValue = document.createElement('span');
    totalValue.className = 'inu-meals__total-value inu-num';
    totalValue.setAttribute('data-inu-meal-sum', '');
    totalValue.textContent = '0';

    const totalOf = document.createElement('span');
    totalOf.className = 'inu-meals__total-of';
    totalOf.textContent = `/ ${rule.total}${data.unit}`;

    total.append(totalLabel, totalValue, totalOf);
    field.appendChild(total);

    const alert = document.createElement('p');
    alert.className = 'inu-meals__alert';
    alert.setAttribute('data-inu-meal-alert', '');
    alert.setAttribute('role', 'status');
    alert.setAttribute('aria-live', 'polite');
    field.appendChild(alert);

    return field;
  }

  /**
   * 食材1つ分の行
   * @param {string} uid
   * @param {string} choice
   * @param {number} index
   * @param {number} max
   * @param {string} unit
   */
  #buildItem(uid, choice, index, max, unit) {
    const item = document.createElement('li');
    item.className = 'inu-meals__item';

    const inputId = `${uid}-meal-${index}`;

    const label = document.createElement('label');
    label.className = 'inu-meals__name';
    label.htmlFor = inputId;
    label.textContent = choice;
    item.appendChild(label);

    const stepper = document.createElement('span');
    stepper.className = 'inu-meals__stepper';

    stepper.appendChild(this.#buildStep('-1', `${choice}を1${unit}減らす`, '−'));

    const input = document.createElement('input');
    input.id = inputId;
    input.className = 'inu-meals__count inu-num';
    input.type = 'number';
    input.inputMode = 'numeric';
    input.min = '0';
    input.max = String(max);
    input.step = '1';
    input.value = '0';
    input.setAttribute('data-inu-meal-input', '');
    input.dataset.choice = choice;
    stepper.appendChild(input);

    stepper.appendChild(this.#buildStep('1', `${choice}を1${unit}増やす`, '＋'));

    item.appendChild(stepper);
    return item;
  }

  /**
   * ＋ / − ボタン1つ
   * @param {string} step
   * @param {string} label
   * @param {string} sign
   */
  #buildStep(step, label, sign) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'inu-meals__step';
    button.setAttribute('data-inu-meal-step', step);
    button.setAttribute('aria-label', label);
    button.textContent = sign;
    return button;
  }

  /**
   * 入力を検算し、合計・案内・注文メモ・カートボタンをそろえる。
   * 入力のたびに呼ぶので、ここだけが状態を書き換える
   */
  #sync() {
    const data = this.#data;
    const rule = this.#rule;
    const property = this.querySelector('[data-inu-meal-property]');

    // 1種類限定。選ぶまで入れさせず、選んだら「フィッシュ 3食」の形で残す
    if (data?.single) {
      const picked = this.#pickedChoice();
      const valid = Boolean(picked) && Boolean(rule) && rule.total > 0;

      const alert = this.querySelector('[data-inu-meal-alert]');
      if (alert) {
        alert.textContent = valid ? '' : data.messages.pick || '食材をお選びください';
        alert.classList.remove('is-error');
      }

      if (property instanceof HTMLInputElement) {
        property.disabled = !valid;
        property.value = valid ? `${picked} ${rule.total}${data.unit}` : '';
      }

      // 選択欄が出ないバリアント（1食など）では制限をかけない
      this.#setAddToCart(rule && rule.total > 0 ? valid : true);
      return;
    }

    const inputs = [...this.querySelectorAll('[data-inu-meal-input]')];

    // 内訳が要らないバリアント。注文メモを送らず、ボタンの制限も外す
    if (!data || !rule || rule.total === 0 || inputs.length === 0) {
      if (property instanceof HTMLInputElement) {
        property.value = '';
        property.disabled = true;
      }
      this.#setAddToCart(true);
      return;
    }

    const picked = inputs
      .map((input) => ({
        choice: input instanceof HTMLInputElement ? (input.dataset.choice ?? '') : '',
        count: input instanceof HTMLInputElement ? Math.max(Number(input.value) || 0, 0) : 0,
      }))
      .filter((entry) => entry.count > 0);

    const sum = picked.reduce((total, entry) => total + entry.count, 0);
    const maxEach = rule.maxEach > 0 ? rule.maxEach : rule.total;
    const over = picked.find((entry) => entry.count > maxEach);

    const sumOutput = this.querySelector('[data-inu-meal-sum]');
    if (sumOutput) sumOutput.textContent = String(sum);

    // 過不足の案内。上限超えを先に出す（そちらのほうが直し方が分かりやすい）
    let message = '';
    if (over) {
      message = this.#format(data.messages.max, maxEach, data.unit);
    } else if (sum < rule.total) {
      message = this.#format(data.messages.short, rule.total - sum, data.unit);
    } else if (sum > rule.total) {
      message = this.#format(data.messages.over, sum - rule.total, data.unit);
    }

    const alert = this.querySelector('[data-inu-meal-alert]');
    if (alert) {
      alert.textContent = message;
      alert.classList.toggle('is-error', Boolean(over) || sum > rule.total);
    }

    const valid = !over && sum === rule.total;

    if (property instanceof HTMLInputElement) {
      property.disabled = !valid;
      property.value = valid ? picked.map((entry) => `${entry.choice} ${entry.count}${data.unit}`).join(' / ') : '';
    }

    this.#setAddToCart(valid);
  }

  /**
   * @param {string} template
   * @param {number} n
   * @param {string} unit
   */
  #format(template, n, unit) {
    return template.replace('{n}', String(n)).replace('{unit}', unit);
  }

  /**
   * カートに入れるボタンの可否。売り切れなど元から押せないものは触らない
   * @param {boolean} enabled
   */
  #setAddToCart(enabled) {
    const rule = this.#rule;
    if (!rule) return;
    if (enabled && rule.available === false) return;

    // ボタンはバリアント切替で差し替わることがあるので、そのつど引き直す
    const button = this.closest('form')?.querySelector('button[name="add"]');
    if (button instanceof HTMLButtonElement) button.disabled = !enabled;
  }
}

if (!customElements.get('inu-meal-choices')) {
  customElements.define('inu-meal-choices', InuMealChoices);
}
